import React, { useEffect, useId, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import type { DocumentationScore as DocumentationScoreData } from '../../types/buildExpansion';
import apiService from '../../services/ApiService';
import { normalizeDocumentationScore } from '../../utils/documentationScore';
import Button from '../ui/Button';

/**
 * Owner-only documentation score panel (collapsible).
 * Measures how completely a build is documented — not certification.
 */

const formatDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const RING_RADIUS = 26;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export interface DocumentationScoreProps {
  score: DocumentationScoreData | null;
  isOwner: boolean;
  /** Needed for the owner recalculate action. */
  vehicleId: number;
  onScoreUpdated?: (score: DocumentationScoreData) => void;
  /** Optional content nested inside the collapsible body (e.g. documenting nudge). */
  children?: React.ReactNode;
  className?: string;
}

export const DocumentationScore: React.FC<DocumentationScoreProps> = ({
  score: scoreProp,
  isOwner,
  vehicleId,
  onScoreUpdated,
  children,
  className = '',
}) => {
  const detailsId = useId();
  const [score, setScore] = useState<DocumentationScoreData | null>(() =>
    normalizeDocumentationScore(scoreProp)
  );
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setScore(normalizeDocumentationScore(scoreProp));
  }, [scoreProp]);

  const total = score ? Math.max(0, Math.min(100, Math.round(score.total))) : null;
  const updated = score ? formatDate(score.calculated_at) : null;
  const recommendations = score ? score.recommendations ?? [] : [];
  const hasChildren = Boolean(children);

  // Completeness is owner-private — never show to visitors.
  if (!isOwner || (!score && !hasChildren)) return null;

  const handleRecalculate = async () => {
    setRecalculating(true);
    setError(null);
    try {
      const next = await apiService.recalculateDocumentationScore(vehicleId);
      if (next) {
        setScore(next);
        onScoreUpdated?.(next);
      }
    } catch (e) {
      setError('Could not recalculate right now. Try again in a moment.');
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <section
      className={`rounded-2xl border border-white/[0.06] bg-[#0c0c0c]/80 p-4 sm:p-5 ${className}`}
      aria-label="Documentation score"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-controls={detailsId}
          className="min-w-0 flex-1 flex items-center gap-3 text-left min-h-[44px] rounded-lg
                     hover:bg-white/[0.03] transition-colors cursor-pointer
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
        >
          <ChevronDownIcon
            className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
            aria-hidden
          />
          <div className="min-w-0">
            <p className="kicker mb-0.5">Completeness</p>
            <p className="font-display text-base sm:text-lg font-semibold tracking-tight text-white">
              Documentation score
              {total != null ? (
                <span className="tabular-nums text-gray-300 font-normal text-sm sm:text-base">
                  {' '}
                  · {total}/100
                </span>
              ) : null}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Only you can see this.</p>
          </div>
        </button>
        {score ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRecalculate}
            loading={recalculating}
            loadingLabel="Recalculating…"
          >
            Recalculate
          </Button>
        ) : null}
      </div>

      {expanded ? (
        <div id={detailsId} className="mt-4 pt-4 border-t border-white/[0.06] space-y-5">
          {children}

          {error && (
            <p className="text-sm text-amber-300" role="alert">
              {error}
            </p>
          )}

          {score ? (
            <>
              <div className="flex flex-col sm:flex-row gap-5 sm:gap-8">
                <div className="flex items-center gap-5 shrink-0">
                  <div className="relative w-24 h-24" aria-hidden>
                    <svg viewBox="0 0 64 64" className="w-24 h-24 -rotate-90">
                      <circle
                        cx="32"
                        cy="32"
                        r={RING_RADIUS}
                        fill="none"
                        strokeWidth="4"
                        className="stroke-white/10"
                      />
                      <circle
                        cx="32"
                        cy="32"
                        r={RING_RADIUS}
                        fill="none"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeDasharray={RING_CIRCUMFERENCE}
                        strokeDashoffset={RING_CIRCUMFERENCE * (1 - (total ?? 0) / 100)}
                        className="stroke-orange-400 transition-[stroke-dashoffset] duration-300 motion-reduce:transition-none"
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center font-display text-3xl font-semibold tracking-tight text-white tabular-nums">
                      {total}
                    </span>
                  </div>
                  <div>
                    <p className="spec-label">of 100</p>
                    {updated && <p className="text-xs text-gray-500 mt-1">Updated {updated}</p>}
                  </div>
                </div>

                {score.sections.length > 0 && (
                  <ul className="flex-1 min-w-0 space-y-2.5 list-none">
                    {score.sections.map((section) => {
                      const max = section.max > 0 ? section.max : 1;
                      const pct = Math.max(0, Math.min(100, (section.score / max) * 100));
                      return (
                        <li key={section.key}>
                          <div className="flex items-baseline justify-between gap-3 text-sm">
                            <span className="text-gray-300">{section.label}</span>
                            <span className="font-mono text-xs text-gray-400 tabular-nums shrink-0">
                              {section.score} / {section.max}
                            </span>
                          </div>
                          <div className="h-1.5 w-full rounded-full bg-white/[0.06] overflow-hidden mt-1" aria-hidden>
                            <div
                              className="h-full rounded-full bg-white/70"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {recommendations.length > 0 && (
                <div className="pt-4 border-t border-white/[0.06]">
                  <h4 className="text-sm font-semibold text-white">Improve this score</h4>
                  <p className="text-xs text-gray-500 mt-0.5">Only you can see these suggestions.</p>
                  <ul className="mt-2.5 space-y-1.5 list-none">
                    {recommendations.map((rec, i) => (
                      <li key={`${rec.section}-${i}`} className="flex items-start gap-2 text-sm text-gray-300">
                        <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-orange-400/80 shrink-0" aria-hidden />
                        {rec.text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default DocumentationScore;
